import { Button, Color, Flex, Icon, Text } from "src/elements";
import { Translate, useTranslation } from "src/i18n";
import { PogAgentFailure } from "../store/api";
import {
  FollowUpAction,
  alternativeFor,
  countApplyResults,
  previewPogFor,
} from "../store/followUp";
import { PogAgentRunResponse } from "../store/types";
import { AgentApplyResults, describeCounts } from "./AgentApplyResults";
import { AgentDiffSummary } from "./AgentDiffSummary";
import styled from "styled-components";
import { ReviewButton } from "../../../pages/SpacePlanning/review/reviewDesign";
import { ReviewColor } from "../../../pages/SpacePlanning/review/reviewDesignTokens";

const MessageSurface = styled(Flex)<{ $embedded: boolean }>`
  ${({ $embedded }) =>
    $embedded &&
    `
    color: ${ReviewColor.ink};
    [data-message-meta] span { color: ${ReviewColor.muted}; font-size: 12px; line-height: 18px; }
    details { font-size: 13px; line-height: 20px; }
    summary { padding: 4px 0; font-weight: 500; }
  `}
`;

const WarningList = styled(Flex)<{ $embedded: boolean }>`
  ${({ $embedded }) =>
    $embedded &&
    `
    padding: 10px;
    background: ${ReviewColor.warningBg};
    border-radius: 8px;
    span { color: ${ReviewColor.warning}; font-size: 12px; line-height: 18px; }
  `}
`;

import { autofixFeedbackOf } from "../../../pages/SpacePlanning/review/autofixFeedback";

type T = Translate<"pogAgent">;

/** Run details after the intent chip: how it ran, not what it was. */
export const metaLine = (response: PogAgentRunResponse, t: T): string =>
  [
    t(`mode.${response.mode}`),
    response.scope && t(`scope.${response.scope}`),
    response.modelUsed && t("card.model", { model: response.modelUsed }),
  ]
    .filter(Boolean)
    .join(" · ");

export const describeError = (error: PogAgentFailure, t: T): string => {
  if (error.kind === "agent")
    return t("errors.agent", { status: error.status, detail: error.detail });
  if (error.kind === "unreachable") return t("errors.unreachable", { detail: error.detail });
  if (error.kind === "batchStalled") return t("errors.batchStalled");

  return error.detail;
};

export const followUpLabel = (action: FollowUpAction, t: T): string => {
  if (action.kind === "resumeMerchflow") {
    return t("actions.resumeMerchflow", {
      next: (action.applyOffset ?? 0) + 1,
      total: action.total ?? 0,
    });
  }

  return t(`actions.${action.kind}`);
};

interface Props {
  embedded?: boolean;
  restrictToPog?: boolean;
  previewAssessment?: { summary: string; blocked: string | null };
  response: PogAgentRunResponse;
  /** Set only on the latest turn — a new prompt replaces the stored plan. */
  followUp: FollowUpAction | null;
  /** A batched apply that stopped part-way; the rows in `response` are what finished. */
  failure?: PogAgentFailure;
  isDisabled: boolean;
  /** Whether this turn's preview is the layout currently drawn on the canvas. */
  isOnCanvas: boolean;
  /**
   * The saved planogram changed after this preview was made, so the preview no
   * longer describes what the agent would do now. Only re-running is offered.
   */
  isStale?: boolean;
  onFollowUp: (action: FollowUpAction) => void;
  onShowOnCanvas: (response: PogAgentRunResponse) => void;
  onRerun?: () => void;
}

export const AgentMessageCard = ({
  embedded = false,
  restrictToPog = false,
  previewAssessment,
  response,
  followUp,
  failure,
  isDisabled,
  isOnCanvas,
  isStale = false,
  onFollowUp,
  onShowOnCanvas,
  onRerun,
}: Props) => {
  const { t } = useTranslation("pogAgent");
  const ActionButton = embedded ? ReviewButton : Button;
  const feedback = autofixFeedbackOf(response.warnings);
  const displayedWarnings = embedded
    ? [...feedback.limits, ...(feedback.partialSearch ? [t("reviewCopy.partialSearch")] : [])]
    : response.warnings;
  const isPreview = response.mode === "preview";
  const isQa = response.intent === "qa";
  const previewPog = previewPogFor(response);
  // Save-as-new sits behind the main button, like "Save New Version" behind
  // Save: in place is the default, a new version is the exception.
  const alternative = followUp && !restrictToPog ? alternativeFor(followUp) : null;
  const counts = countApplyResults(response);
  // A merged batch carries no message of its own; the card sums it up here.
  const headline =
    response.message ||
    (response.results.length
      ? t("card.mergedSummary", { total: counts.total, parts: describeCounts(counts, t) })
      : t("card.noMessage"));

  return (
    <MessageSurface
      $embedded={embedded}
      column
      gap="8px"
      padding={embedded ? "12px" : "10px 12px"}
      background={Color.white}
      borderColor={embedded ? ReviewColor.line : Color.spaceGray}
      borderRadius={embedded ? "12px" : "4px"}
      maxWidth="100%"
    >
      {restrictToPog && isPreview && previewPog && headline.length > 450 ? (
        <>
          <Text variant="body3">{t("reviewCopy.previewReady")}</Text>
          <details>
            <summary>{t("reviewCopy.whyFix")}</summary>
            <Text variant="body3" wordBreak="break-word">
              {headline}
            </Text>
          </details>
        </>
      ) : (
        <Text variant="body3" wordBreak="break-word">
          {headline}
        </Text>
      )}

      {failure && response.applyBatch && (
        <Flex
          gap="6px"
          align="center"
          padding="8px 10px"
          background={Color.alertHighBg}
          borderColor={Color.alertHighBorder}
          borderRadius="4px"
        >
          <Icon name="alert" color={Color.red} size={14} />

          <Text variant="small2" color={Color.alertHighText} wordBreak="break-word">
            {t("card.stoppedEarly", {
              done: response.applyBatch.count,
              total: response.applyBatch.total,
              error: describeError(failure, t),
            })}
          </Text>
        </Flex>
      )}

      {/* QA never edits a POG, so it has neither a diff nor apply results. */}
      {!isQa && isPreview && <AgentDiffSummary embedded={embedded} diff={response.diff} />}

      {!isQa && !isPreview && <AgentApplyResults response={response} />}

      {displayedWarnings.length > 0 && (
        <WarningList $embedded={embedded} column gap="8px">
          {displayedWarnings.map((warning) => (
            <Flex key={warning} gap="6px" align="center">
              <Icon name="warningCircle" color={Color.yellow} size={12} />

              <Text variant="small2" color={Color.warningText} wordBreak="break-word">
                {warning}
              </Text>
            </Flex>
          ))}
        </WarningList>
      )}

      {/* What the agent took the prompt for, in the merchandiser's words. Makes
          a wrong classification visible at a glance. */}
      <Flex gap="8px" align="center" flexWrap="wrap" data-message-meta>
        <Flex
          padding="1px 7px"
          background={embedded ? ReviewColor.subtle : isQa ? Color.lightGray : Color.greenSmoke}
          borderRadius="10px"
          data-testid="pog-agent-intent-chip"
        >
          <Text variant="caption2" color={isQa ? Color.textSecondary : Color.primary}>
            {t(`intent.${response.intent}`)}
          </Text>
        </Flex>

        <Text variant="small2" color={Color.textSecondary}>
          {restrictToPog
            ? `Claude Opus · ${t(isPreview ? "reviewCopy.preview" : isQa ? "reviewCopy.answer" : "reviewCopy.saved")}`
            : metaLine(response, t)}
        </Text>
      </Flex>

      {isStale && (
        <Flex gap="6px" align="center">
          <Icon name="warningCircle" color={Color.yellow} size={12} />

          <Text variant="small2" color={Color.warningText} wordBreak="break-word">
            {t("card.stale")}
          </Text>
        </Flex>
      )}

      {previewAssessment && <Text variant="body3">{previewAssessment.summary}</Text>}
      {previewAssessment?.blocked && (
        <Text variant="small2" color={Color.alertHighText}>
          {previewAssessment.blocked}
        </Text>
      )}
      {(followUp || previewPog || (isStale && onRerun)) && (
        <Flex gap="6px" flexWrap="wrap">
          {isStale && onRerun && (
            <ActionButton size="small" color="primary" isDisabled={isDisabled} onClick={onRerun}>
              {t("actions.rerun")}
            </ActionButton>
          )}

          {followUp && embedded && !alternative ? (
            <ReviewButton
              size="small"
              color={followUp.scope === "merchflow" ? "greenSmoke" : "primary"}
              isDisabled={isDisabled || !!previewAssessment?.blocked}
              onClick={() => onFollowUp(followUp)}
            >
              {followUpLabel(followUp, t)}
            </ReviewButton>
          ) : followUp ? (
            <Button
              size="small"
              color={followUp.scope === "merchflow" ? "greenSmoke" : "primary"}
              isDisabled={isDisabled || !!previewAssessment?.blocked}
              onClick={() => onFollowUp(followUp)}
              dropdown={
                alternative
                  ? [
                      {
                        label: followUpLabel(alternative, t),
                        onClick: () => onFollowUp(alternative),
                      },
                    ]
                  : undefined
              }
            >
              {followUpLabel(followUp, t)}
            </Button>
          ) : null}

          {previewPog && (
            <ActionButton
              size="small"
              color="greenSmoke"
              variant={isOnCanvas ? "inverted" : "default"}
              // A stale preview would draw an old layout over the newly saved one.
              isDisabled={isDisabled || isOnCanvas || isStale}
              iconLeft={isOnCanvas ? { name: "checkmark" } : false}
              onClick={() => onShowOnCanvas(response)}
            >
              {isOnCanvas ? t("actions.onCanvas") : t("actions.showOnCanvas")}
            </ActionButton>
          )}
        </Flex>
      )}
    </MessageSurface>
  );
};
